import { Currency, Token } from '@uniswap/sdk-core'
import { useAccountDrawer } from 'components/AccountDrawer/MiniPortfolio/hooks'
import { PortfolioLogo } from 'components/AccountDrawer/MiniPortfolio/PortfolioLogo'
import PortfolioRow from 'components/AccountDrawer/MiniPortfolio/PortfolioRow'
import Column from 'components/Column'
import Row from 'components/Row'
import { nativeOnChain } from 'constants/tokens'
import { RobinhoodHolding } from 'hooks/useRobinhoodPortfolio'
import styled from 'lib/styled-components'
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUpdateAtom } from 'jotai/utils'
import { pendingSwapOutputCurrencyAtom } from 'state/swap/pendingSwapCurrency'
import { EllipsisStyle, ThemedText } from 'theme/components'
import { UniverseChainId } from 'uniswap/src/types/chains'
import { NumberType, useFormatter } from 'utils/formatNumbers'

export const ROBINHOOD_HOLDINGS_ENABLED = process.env.REACT_APP_ENABLE_ROBINHOOD_CHAIN === 'true'
const ETH_LOGO = 'https://assets.coingecko.com/coins/images/279/small/ethereum.png'

const SectionHeader = styled(Row)`
  padding: 12px 16px 4px;
  justify-content: space-between;
`
const TokenNameText = styled(ThemedText.SubHeader)`
  ${EllipsisStyle}
`
const TokenBalanceText = styled(ThemedText.BodySecondary)`
  ${EllipsisStyle}
`

/** Build the sdk-core Currency for a held 4663 asset so it can be selected into the swap. */
function holdingToCurrency(holding: RobinhoodHolding): Currency {
  if (holding.isNative) {
    return nativeOnChain(UniverseChainId.Robinhood)
  }
  return new Token(UniverseChainId.Robinhood, holding.address, holding.decimals, holding.symbol, holding.name)
}

function HoldingRow({ holding, onSelect }: { holding: RobinhoodHolding; onSelect: (h: RobinhoodHolding) => void }) {
  const { formatNumber } = useFormatter()
  const qty = formatNumber({ input: holding.quantity, type: NumberType.TokenNonTx })
  const usd =
    holding.usdValue !== undefined
      ? formatNumber({ input: holding.usdValue, type: NumberType.PortfolioBalance })
      : undefined

  return (
    <PortfolioRow
      onClick={() => onSelect(holding)}
      left={<PortfolioLogo chainId={4663} images={holding.isNative ? [ETH_LOGO] : [holding.logoURI]} size={40} />}
      title={<TokenNameText>{holding.symbol}</TokenNameText>}
      descriptor={<TokenBalanceText>{holding.name}</TokenBalanceText>}
      right={
        <Column style={{ alignItems: 'flex-end' }}>
          {usd && <ThemedText.SubHeader>{usd}</ThemedText.SubHeader>}
          <TokenBalanceText>
            {qty} {holding.symbol}
          </TokenBalanceText>
        </Column>
      }
    />
  )
}

/**
 * Robinhood Chain (4663) holdings section for the wallet tray. Uniswap's portfolio backend doesn't
 * index 4663, so this is our own hybrid view (Blockscout discovery + on-chain balances). Presentational
 * data comes from the owning Tokens tab (which runs `useRobinhoodPortfolio` once). Clicking a row
 * selects that token into the swap as the output currency (4663 has no token-details page to route
 * through, so we hand the currency to SwapForm via `pendingSwapOutputCurrencyAtom`) and closes the drawer.
 * Renders nothing when there's nothing to show.
 */
export function RobinhoodTokens({ holdings, discoveryLimited }: { holdings: RobinhoodHolding[]; discoveryLimited: boolean }) {
  const navigate = useNavigate()
  const accountDrawer = useAccountDrawer()
  const setPendingOutputCurrency = useUpdateAtom(pendingSwapOutputCurrencyAtom)

  const onSelect = useCallback(
    (holding: RobinhoodHolding) => {
      setPendingOutputCurrency(holdingToCurrency(holding))
      navigate('/swap')
      accountDrawer.close()
    },
    [setPendingOutputCurrency, navigate, accountDrawer],
  )

  if (holdings.length === 0) {
    return null
  }

  return (
    <Column>
      <SectionHeader>
        <ThemedText.SubHeaderSmall color="neutral2">Robinhood Chain</ThemedText.SubHeaderSmall>
        {discoveryLimited && (
          <ThemedText.LabelMicro color="neutral3">discovery temporarily limited</ThemedText.LabelMicro>
        )}
      </SectionHeader>
      {holdings.map((h) => (
        <HoldingRow key={h.address} holding={h} onSelect={onSelect} />
      ))}
    </Column>
  )
}
